import 'bootstrap/dist/css/bootstrap.min.css';
import { AnimatePresence } from 'framer-motion';
import React, { useContext, useEffect, useImperativeHandle, useState } from 'react';
import { FaRegTrashAlt } from "react-icons/fa";
import { Separators } from '../../Constants.ts';
import MandatoryStar from '../../UIComponents/MandatoryStar.tsx';
import '../../Utilities/Extensions.ts';
import { RoleResponseModel } from '../../_core/_model.ts';
import { GetFilters } from '../../_core/_request.ts';
import { useAppContext } from '../../context/AppContext.tsx';
import { AuthContext } from '../../context/AuthContext.js';
import { useToast } from '../../context/ToastContext.tsx';
import { defaultTheme } from '../../styles/themes/defaultTheme.js';
import DiensionDropdown from '../Tools/DiensionDropdown.tsx';
import FilterConditionBuilder from './FilterConditionBuilder.tsx';
import WeightInput from './WeightInput.js';
import { RoleCondition, RoleModel } from './_core/_model.ts';
import { SaveRole } from './_core/_request.ts';

const tFilterString = "Col" + Separators.column + "=" + Separators.column + "" + Separators.column + "Or";// +
// Separators.row + "Col" + Separators.column + "=" + Separators.column + "" + Separators.column + "Or" +
// Separators.row + "Col" + Separators.column + "=" + Separators.column + "" + Separators.column;
interface request {
  roleId: string | null,
  onClose: any,
  preFills: rolePreFills | undefined,
  canSaveCall: (canSave: boolean) => void
}
interface rolePreFills {
  dataMapCode: string,
  sConditions: string
}
const RoleEditor = React.forwardRef(({ roleId, onClose, preFills, canSaveCall }: request, ref) => {
  const [isLoading, setIsLoading] = useState(true);
  const { roles, refreshRoles } = useAppContext();
  const [showDrawer, setShowDrawer] = useState(false);
  const [initialFormData, setInitialFormData] = useState<RoleModel | null>(null);
  const { user: loggedInUser } = useContext(AuthContext);
  const { showToast } = useToast();

  useImperativeHandle(ref, () => ({
    SaveRole: handleSave,
    CanSave: canSave
  }));
  const getRowsFromString = (str: any) => {
    if (str == null || str == "")
      return [getNewRow([])];
    let rArr = str.split(Separators.row);
    let rows: RoleCondition[] = [];
    for (var index in rArr) {
      let tRow = rArr[index].split(Separators.column);
      if (tRow.length > 2) {
        rows.push({
          selected: false,
          id: parseInt(index),
          dataMapColumn: tRow[0],
          oper: tRow[1],
          value: tRow[2].replace(/^'(.*)'$/, '$1'),
          andOr: tRow[3]
        });
      }
    }
    return rows;
  }

  const getStringFromRows = (rows) => {
    let str = "";
    for (var indx in rows) {
      str += rows[indx].dataMapColumn + Separators.column;
      let operator = (rows[indx].oper == "startwith") ? "like" : (rows[indx].oper == "endwith") ? "like" : rows[indx].oper;
      str += operator + Separators.column;
      let value = (rows[indx].oper == "startwith") ? rows[indx].value + "%" : (rows[indx].oper == "endwith") ? "%" + rows[indx].value : rows[indx].value;
      str += "'" + value + "'" + Separators.column;
      str += rows[indx].andOr + Separators.column;
      str += Separators.row;
    }
    return str;
  }

  const [formData, setFormData] = useState<RoleModel>({
    roleID: 0,
    filterCode: '',
    roleCode: "",
    roleName: "",
    description: "",
    roleType: "",
    weight: 4,
    sqlObjectIDPrefix: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    status: true,
    dataMapCode: '',
    notes: '',
    filterConditions: "",
    filterRules: getRowsFromString(tFilterString)
  });

  useEffect(() => {
    if (roleId != null) {
      setIsLoading(true);
      let roleData = roles.find((x) => x.roleID == parseInt(roleId));
      if (typeof roleData != 'undefined') {
        GetFilters({ roleID: (roleData.roleID != null) ? roleData.roleID : 0 }).then((f) => {
          const fData = f.data.length > 0 ? f.data[0].filterConditions : "";
          const loadedData = {
            roleID: roleData.roleID,
            apiState: { message: "", progress: false, success: false },
            filterCode: "",
            roleCode: roleData.roleCode,
            roleName: roleData.roleName,
            description: roleData.description,
            roleType: roleData.roleType,
            weight: roleData.weight,
            sqlObjectIDPrefix: "",
            effectiveDate: roleData.effectiveDate,
            status: roleData.status === "Active",
            dataMapCode: roleData.dataMapCode,
            notes: roleData.notes,
            filterConditions: fData,
            filterRules: getRowsFromString(fData)
          };
          setFormData(loadedData);
          setInitialFormData(loadedData); // Store initial data
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    } else {
      const newFormData = {
        roleID: 0,
        apiState: {
          message: "",
          progress: false,
          success: false
        },
        filterCode: '',
        roleCode: "",
        roleName: "",
        description: "",
        roleType: "",
        weight: 4,
        sqlObjectIDPrefix: '',
        effectiveDate: new Date().toISOString().split('T')[0],
        status: true,
        dataMapCode: (typeof preFills != 'undefined') ? preFills.dataMapCode : "",
        notes: '',
        filterConditions: "",
        filterRules: (typeof preFills != 'undefined') ? getRowsFromString(preFills.sConditions) : getRowsFromString(tFilterString)
      };
      setFormData(newFormData);
      setInitialFormData(newFormData); // Store initial data
      setIsLoading(false); // no roleId case
    }
  }, []);

  const isFormDirty = () => {
    if (!initialFormData) return false;

    // Compare simple fields
    const simpleFields = ['roleCode', 'roleName', 'description', 'weight',
      'effectiveDate', 'status', 'dataMapCode', 'notes'];
    for (const field of simpleFields) {
      if (formData[field] !== initialFormData[field]) {
        return true;
      }
    }

    // Compare filterRules
    if (formData.filterRules.length !== initialFormData.filterRules.length) {
      return true;
    }

    for (let i = 0; i < formData.filterRules.length; i++) {
      const currentRule = formData.filterRules[i];
      const initialRule = initialFormData.filterRules[i];

      if (!initialRule ||
        currentRule.dataMapColumn !== initialRule.dataMapColumn ||
        currentRule.oper !== initialRule.oper ||
        currentRule.value !== initialRule.value ||
        currentRule.andOr !== initialRule.andOr) {
        return true;
      }
    }

    return false;
  };



  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  const getNewRow = (tArr) => {
    return {
      selected: false,
      id: tArr.length,
      dataMapColumn: "",
      oper: "=",
      value: "",
      andOr: "Or"
    }
  }

  const addNewFilterRule = () => {
    setFormData(prev => ({
      ...prev,
      filterRules: [
        ...prev.filterRules,
        getNewRow(prev.filterRules)
      ]
    }));
  };

  const getRoleDirtyFields = (cItem: RoleResponseModel) => {
    let dirtyFields: string[] = [];
    var considerFields = ["roleName", "roleCode", "effectiveDate", "status", "description", "dataMapCode", "notes", "weight", "filterConditions"];
    if (initialFormData != null) {
      for (var k in cItem) {
        if (considerFields.indexOf(k) > -1) {
          if (initialFormData[k] != cItem[k]) {
            if (k == "status") {
              if ((cItem[k].toLowerCase() == "active") != initialFormData[k])
                dirtyFields.push(k);
            }
            else
              dirtyFields.push(k);
          }
        }
      }
    }
    return dirtyFields;
  }

  const handleSave = (updateProgress: (msg: string | null) => void) => {

    let saveVal: RoleResponseModel = {
      createdBy: loggedInUser.userID,
      dimensionID: 0,
      roleID: formData.roleID,
      dimensionName: formData.dataMapCode,
      //filterCode: formData.filterCode,
      roleCode: formData.roleCode,
      roleName: formData.roleName,
      description: formData.description,
      weight: formData.weight,
      // sqlObjectIDPrefix: formData.sqlObjectIDPrefix,
      effectiveDate: formData.effectiveDate,
      status: (formData.status) ? "Active" : "Inactive",
      roleType: "EPM Role",
      dataMapCode: formData.dataMapCode,
      notes: formData.notes,
      filterConditions: getStringFromRows(formData.filterRules),
      _source: "",
      dirtyFields: []
    };
    saveVal.dirtyFields = getRoleDirtyFields(saveVal);
    updateProgress("Save in progress");
    // saveVal.roleCode = formData.roleName.replace(/ /gi, "_");
    SaveRole(saveVal).then((x) => {
      if (typeof x.data == 'number') {
        setTimeout(() => {
          updateProgress("Updating Roles");
          refreshRoles([], (r) => {
            onClose();
          });
        }, 200);
        updateProgress(x.data);
        showToast({
          message: "Saved successfully",
          type: 'success',
          duration: 5000
        })
      }
      else {
        updateProgress(null);
        showToast({
          message: x.data,
          type: 'warning',
          duration: 5000
        })
      }
    })
  };
  const canSave = () => {
    // Check if form is dirty
    if (!isFormDirty()) {
      return false;
    }

    // Original validation checks
    if (formData.roleName.isEmpty() ||
      formData.dataMapCode.isEmpty() ||
      formData.filterRules.length === 0) {
      return false;
    }

    // Check if all filter rules have required fields
    return formData.filterRules.every(rule =>
      !rule.dataMapColumn.isEmpty() && !rule.value.isEmpty()
    );
  };



  useEffect(() => {
    canSaveCall(canSave());
  }, [formData])
  return (
    isLoading ?
      <div className="card-body px-0 pt-2 bg-theme-card">
        <div className="row g-4">
          <div className="col-md-12 px-0 mx-0">
            <div className="placeholder-glow mx-2">
              <span className="placeholder col-12 rounded-3" style={{ height: "35px", display: "block" }}></span>
            </div>
          </div>
          <div className='col-10 mx-4 my-1'>
            <div className='row'>
              <div className='col-5'>
                <div className="placeholder-glow d-flex flex-row gap-1 align-items-center">
                  <span className="placeholder col-2" style={{ height: "10px" }}></span>
                  <span className="placeholder col-10 rounded-3" style={{ height: "30px" }}></span>
                </div>
              </div>
              <div className='col-5'>
                <div className="placeholder-glow d-flex flex-row gap-1 align-items-center">
                  <span className="placeholder col-2" style={{ height: "10px" }}></span>
                  <span className="placeholder col-10 rounded-3" style={{ height: "30px" }}></span>
                </div>
              </div>
            </div>
            <div className='row mt-4'>
              <div className='col-5'>
                <div className="placeholder-glow d-flex flex-row gap-1 align-items-center">
                  <span className="placeholder col-2" style={{ height: "10px" }}></span>
                  <span className="placeholder col-10 rounded-3" style={{ height: "30px" }}></span>
                </div>
              </div>
              <div className='col-5'>
                <div className="placeholder-glow d-flex flex-row gap-1 align-items-center">
                  <span className="placeholder col-2" style={{ height: "10px" }}></span>
                  <span className="placeholder col-10 rounded-3" style={{ height: "30px" }}></span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-5">
            <div className="placeholder-glow">
              <span className="placeholder col-10 mb-2"></span>
              <span className="placeholder col-12 rounded-3" style={{ height: "38px", display: "block" }}></span>
            </div>
          </div>
          <div className="col-md-5">
            <div className="placeholder-glow">
              <span className="placeholder col-10 mb-2"></span>
              <span className="placeholder col-12 rounded-3" style={{ height: "38px", display: "block" }}></span>
            </div>
          </div>
        </div>

        <div className="mt-5 mx-2">
          <h5 className="placeholder-glow mx-2">
            <span className="placeholder col-4"></span>
          </h5>
          <table className="table mt-3">
            <thead>
              <tr>
                {['Select', 'Column', 'Operator', 'Value', 'And/Or'].map((text, i) => (
                  <th key={i}><span className="placeholder col-6"></span></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(3)].map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <td key={j}>
                      <span className="placeholder col-12" style={{ height: "32px", display: "block" }}></span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> :
      <div className="card-body px-0 pt-2 bg-theme-card">
        <div className="row mb-1 mx-0">
          {/* <div className="col-md-4 mb-3">
          <label className="form-label fw-bold">SetID:</label>
          <input
            type="text"
            name="setID"
            value={formData.setID}
            onChange={handleChange}
            className="form-control"
            style={{ borderColor: defaultTheme.colors.border }}
          />
        </div> */}
          <div className="col-md-12 px-0 mx-0">
            <div className="d-flex align-items-center gap-2">
              <div className='w-100 d-flex flex-row gap-1'>
                <input
                  type="text"
                  name="roleName"
                  placeholder='Enter Role Name'
                  value={formData.roleName}
                  onChange={handleChange}
                  className="form-control j_r_name"
                  style={{ fontSize: "16px", textIndent: "8px" }}
                />
                <MandatoryStar />
              </div>
            </div>
          </div>
          <div className='bg-gray row mx-0 ms-0 pt-3' style={{ marginTop: "8px" }}>
            <div className="col-md-5 mb-3 j_r_dim">
              <div className='d-flex flex-row align-items-center gap-2'>
                <label className="text-nowrap d-flex" style={{ width: "130px", fontSize: "12px" }}>Dimensions:<MandatoryStar /></label>
                <DiensionDropdown iniVal={formData.dataMapCode} onChange={(v) => {
                  setFormData(prev => ({
                    ...prev,
                    dataMapCode: v,
                    filterRules: [getNewRow([])]
                  }));
                }} />
              </div>
            </div>
          </div>
          {/* <div className="col-md-4 mb-3">
          <label className="form-label fw-bold">Filter Code:</label>
          <input
            type="text"
            name="filterCode"
            value={formData.filterCode}
            onChange={handleChange}
            className="form-control"
            style={{ borderColor: defaultTheme.colors.border }}
          />
        </div> */}
          {/* <div className="col-md-4 mb-3">
          <label className="form-label fw-bold">SQL Object ID Prefix:</label>
          <input
            type="text"
            name="sqlObjectIDPrefix"
            value={formData.sqlObjectIDPrefix}
            onChange={handleChange}
            className="form-control"
            style={{ borderColor: defaultTheme.colors.border }}
          />
        </div> */}
        </div>

        {/* Status Section */}
        <div className="row mx-0">

          <div className="col-md-5 mb-3 r_desc">
            <label className="form-label fw-500">Description:</label>
            <textarea
              name="description"
              placeholder='Enter Role Description'
              value={formData.description}
              onChange={handleChange}
              className="form-control"
              style={{ borderColor: defaultTheme.colors.border, resize: 'none' }}
            />
          </div>
          <div className="col-md-5 mb-3 j_r_notes">
            <label className="form-label fw-500">Notes:</label>
            <textarea
              name="notes"
              value={formData.notes}
              placeholder='Enter few line of Note about this Role'
              onChange={handleChange}
              className="form-control"
              style={{ borderColor: defaultTheme.colors.border, resize: 'none' }}
            />
          </div>
        </div>

        {/* Filter Rules Section */}
        <div className='px-0 j_r_sel'>
          <div className="d-flex justify-content-between align-items-center p-3 border" style={{ borderRadius: "4px 4px 0px 0px" }}>
            <h6 className="card-title fw-500 mb-0">Selection Criteria</h6>
            <div className='d-flex flex-row align-items-center gap-2'>
              {/* <div className="form-check form-switch">
                    <input
                      type='checkbox'
                      role="switch"
                      className="form-check-input form-switch"
                      name="status"
                      key="status"
                      checked={formData.status}
                      onChange={(e) => {
                        let checked = e.currentTarget.checked;
                        setFormData(prev => ({
                          ...prev,
                          status: checked
                        }));
                      }}
                      style={{ borderColor: defaultTheme.colors.border }}
                    />
                    <label className="form-check-label" htmlFor="replacingFormerUser">
                      {
                        formData.status ? "Enabled" : "Disabled"
                      }
                    </label>
                  </div> */}
            </div>
          </div>
          <FilterConditionBuilder {...{
            dataMapCode: formData.dataMapCode,
            myConditions: formData.filterRules,
            updateRules: (rules) => {
              let prev = formData;
              prev.filterRules = rules;
              setFormData({ ...prev });
            }
          }} />

        </div>
        {/* Action Buttons */}
        <div className="d-flex flex-wrap justify-content-between gap-2 mt-4 mx-3">
          <div>
            <AnimatePresence>
              {
                formData.filterRules.length > 0 && formData.filterRules.filter((x) => x.selected && x.dataMapColumn != "").length > 0 &&
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => {
                    let fData = formData;
                    if (formData.filterRules.filter((x) => x.selected).length == formData.filterRules.length) {
                      fData.filterRules = [getNewRow([""])];
                    }
                    else {
                      fData.filterRules = fData.filterRules.filter((x) => !x.selected);
                    }
                    setFormData({ ...fData });
                  }}
                >
                  <div className='d-flex flex-row align-items-center gap-2'>
                    <FaRegTrashAlt />
                    {
                      (formData.filterRules.length > 1 || formData.filterRules.filter((x) => x.selected).length > 1) ?
                        (formData.filterRules.filter((x) => x.selected).length == 1) ? "Delete selected Row" :
                          "Delete selected row(s)" : "Clear value"
                    }
                  </div>
                </button>
              }
            </AnimatePresence>
          </div>
        </div>
      </div>
  );
});

export default RoleEditor;